import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge nezná naše `@theme` tokeny z index.css a musí se o nich dozvědět, jinak
 * třídy nesprávně zařadí do skupin — s dvěma různými, oběma tichými následky:
 *
 *  1. Špatné sloučení: `text-phx-md` (velikost) i `text-phx-text-muted` (barva) vypadají jako
 *     `text-*`, takže je tailwind-merge bez konfigurace považuje za tutéž skupinu a jednu z nich
 *     ZAHODÍ. `cn('text-phx-xs', 'text-phx-danger')` tak vracelo jen `text-phx-danger` — pole
 *     ztratilo velikost písma a spadlo na 13px z body.
 *  2. Chybějící sloučení: `p-phx-5` a `p-4` naopak obě přežily a vyhrála ta pozdější v CSS,
 *     takže override nefungoval.
 *
 * Klíče v `theme` odpovídají 1:1 namespacům `@theme` v index.css (`--text-*` → `text`,
 * `--spacing-*` → `spacing`, `--radius-*` → `radius`, `--shadow-*` → `shadow`). Barvy
 * (`--color-phx-*`) registrovat netřeba — neznámé `bg-*`/`text-*`/`border-*` hodnoty
 * tailwind-merge správně klasifikuje jako barvu sám.
 *
 * PŘI ZMĚNĚ `@theme` V index.css AKTUALIZUJ I TYTO SEZNAMY — jinak se tiché slučování vrátí.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      // --text-phx-* (velikosti písma)
      text: [
        'phx-3xs',
        'phx-2xs',
        'phx-xs',
        'phx-sm',
        'phx-md',
        'phx-lg',
        'phx-xl',
        'phx-h3',
        'phx-h2',
        'phx-h1',
      ],
      // --spacing-phx-* (padding, margin, gap, width, height, …)
      spacing: [
        'phx-0',
        'phx-1',
        'phx-1_5',
        'phx-2',
        'phx-2_5',
        'phx-3',
        'phx-3_5',
        'phx-4',
        'phx-4_5',
        'phx-5',
        'phx-6',
        'phx-7',
        'phx-8',
        'phx-9',
        'phx-10',
        'phx-11',
        'phx-control-xs',
        'phx-control-sm',
        'phx-control',
        'phx-control-lg',
        'phx-control-xl',
        'phx-row',
        'phx-row-dense',
        'phx-toolbar',
      ],
      // --radius-phx-*
      radius: ['phx-xs', 'phx-sm', 'phx', 'phx-md', 'phx-lg'],
      // --shadow-phx-*
      shadow: ['phx-xs', 'phx-sm', 'phx-md', 'phx-lg', 'phx-focus'],
      // --font-weight-phx-*. Registrace je tu KRITICKÁ: `font-*` je nejednoznačné
      // (rodina vs. váha), takže bez ní tailwind-merge bere `font-phx-semibold` jako
      // rodinu — `cn('font-phx-semibold','font-mono')` zahodí váhu a naopak.
      'font-weight': ['phx-regular', 'phx-medium', 'phx-semibold', 'phx-bold'],
      // --leading-phx-*
      leading: ['phx-tight', 'phx-normal', 'phx-loose'],
      // --tracking-phx-*
      tracking: ['phx-wide'],
      // --ease-phx-*
      ease: ['phx-out'],
    },
  },
});

/**
 * Utility pro spojování Tailwind CSS tříd.
 * Kombinuje clsx pro podmíněné třídy a tailwind-merge pro deduplikaci.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
